import { apiClient } from "@/services/api/axiosClient";
import type { ApiResponse } from "@/services/api/api.types";
import { API_ENDPOINTS } from "@/services/api/endpoints";

export type ProtestStatus = "submitted" | "inprogress" | "approved" | "rejected" | "pending";

export type Protest = {
  id: string;
  userId: string;
  propertyId: string;
  status: ProtestStatus;
  createdAt: string;
  updatedAt?: string;
  protestYear?: number;
  savings?: number;
  reductionPercentage?: number;
};

export type Milestone = {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  achieved: boolean;
  achievedAt?: string;
};

export type AchievementsData = {
  active_protest: number;
  completed: number;
  won: number;
  lost: number;
  total_achievement: number;
  protests: Protest[];
  milestones: Milestone[];
  statistics: {
    totalProtests: number;
    completedProtests: number;
    wonProtests: number;
    lostProtests: number;
    totalSavings: number;
    averageReduction: number;
  };
};

export type ProtestsExemptionsListResponse = {
  payments: ProtestsExemptionsPayment[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
};

export type ProtestsExemptionsPropertyDetails = {
  id?: number;
  property_id?: string;
  name?: string | null;
  unit_number?: string;
  property_type?: string;
  full_address?: string;
  city?: string;
  state?: string;
  zip?: string;
};

export type ProtestsExemptionsUserDetails = {
  user_id?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  full_name?: string;
};

export type SupportingDocumentProperty = {
  id?: number;
  property_id?: string;
  document_type?: string;
  document?: string;
  user_id?: number;
  status?: string;
  created_at?: string;
  updated_at?: string;
};

export type HomesteadExemptionDocument = {
  document_id?: string;
  property_id?: string;
  user_id?: number;
  document_url?: string;
  homestead_submission_id?: number;
  homestead_document_url?: string;
  exemption_submission_id?: number;
  exemption_document_url?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
};

export type PropertyHomesteadDocument = {
  id?: number;
  property_id?: string;
  user_id?: number;
  document_type?: string;
  document_side?: string;
  document?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
};

export type PropertyProtestDocument = {
  id?: number;
  document_id?: string;
  property_id?: string;
  signed_andy_app_agreement_document_url?: string;
  signed_andy_app_agreement?: boolean;
  signed_aoa?: string;
  signed_dtr?: string;
  signed_dtr_document_url?: string;
  signed_notice_of_protest?: boolean;
  signed_notice_of_protest_document_url?: string;
  signed_document_url?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type PropertyProtestKyc = {
  kyc_id?: string;
  property_id?: string;
  document_type?: string;
  document_url?: string;
  driving_licence_url?: string;
  created_at?: string;
  updated_at?: string;
};

export type PropertySettlementWaiverDocument = {
  id?: number;
  property_id?: string;
  settlement_waiver_document?: string;
  created_at?: string;
  updated_at?: string;
};

export type ProtestsExemptionsPayment = {
  id: number;
  payment_intent_id?: string;
  amount?: number;
  year?: number;
  type?: "protest" | "homestead";
  protest_status?: string | null;
  homestead_status?: string | null;
  paid?: boolean;
  paid_at?: string | null;
  created_at?: string;
  updated_at?: string;
  status?: string;
  propertyId?: string;
  property_details?: ProtestsExemptionsPropertyDetails;
  property_protest_document?: PropertyProtestDocument[] | null;
  property_protest_documents?: PropertyProtestDocument[] | null;
  homestead_exemption_documents?: HomesteadExemptionDocument[] | null;
  property_homestead_document?: PropertyHomesteadDocument[] | null;
  property_protest_kyc?: PropertyProtestKyc[] | null;
  property_settlement_waiver_document?: PropertySettlementWaiverDocument[] | null;
  supporting_document_property?: SupportingDocumentProperty[] | null;
  supporting_documents_privacy?: SupportingDocumentProperty[] | null;
  user_details?: ProtestsExemptionsUserDetails;
};

export const achievementsService = {
  async getAchievements(): Promise<ApiResponse<AchievementsData>> {
    const { data } = await apiClient.get<ApiResponse<AchievementsData>>(
      API_ENDPOINTS.PROPERTY.USER_ACHIEVEMENT
    );
    return data;
  },
};

export const exemptionsService = {
  async getExemptions(params?: {
    type?: "protest" | "homestead";
    page?: number;
    page_size?: number;
    protest_status?: string;
    homestead_status?: string;
  }): Promise<ApiResponse<ProtestsExemptionsListResponse>> {
    const searchParams = new URLSearchParams();
    searchParams.set("page", String(params?.page ?? 1));
    searchParams.set("page_size", String(params?.page_size ?? 10));
    searchParams.set("type", params?.type ?? "protest");
    searchParams.set("protest_status", params?.protest_status ?? "");
    searchParams.set("homestead_status", params?.homestead_status ?? "");

    const { data } = await apiClient.get<ApiResponse<ProtestsExemptionsListResponse>>(
      API_ENDPOINTS.PROPERTY.PROTEST_EXEMPTIONS_LIST(searchParams.toString())
    );
    return data;
  },
};
